// src/hooks/useNotify.ts
import { useCallback } from "react";
import { notifications } from "@mantine/notifications";
import { useNotificationContext } from "@/context/NotificationContext";
import type { AppNotification, CreateNotificationPayload } from "@/api/notifications";

type NotificationType = "success" | "error" | "info" | "warning";

const TYPE_COLOR: Record<NotificationType, string> = {
  success: "green",
  error: "red",
  warning: "yellow",
  info: "blue",
};

export type NotifyOptions = {
  type?: NotificationType;
  title: string;
  message?: string;
  showToast?: boolean;
  toastDuration?: number;
  showInBar?: boolean;
  barDuration?: number;
  actionUrl?: string;
  category?: string;
  autoReadAfterMs?: number;
};

export function useNotify() {
  const ctx = useNotificationContext();

  const notify = useCallback(
    async (options: NotifyOptions) => {
      const {
        type = "info",
        title,
        message,
        showToast = true,
        toastDuration = 5000,
        showInBar = true,
        barDuration,
        actionUrl,
        category,
        autoReadAfterMs,
      } = options;

      // Always show Mantine toast first (optimistic)
      if (showToast) {
        notifications.show({
          title,
          message: message || undefined,
          color: TYPE_COLOR[type],
          autoClose: toastDuration,
        });
      }

      // Persist to DB if showInBar
      if (showInBar) {
        const payload: CreateNotificationPayload = {
          title,
          message,
          type,
          category,
          action_url: actionUrl,
          show_toast: showToast,
          toast_duration: toastDuration,
          bar_duration: barDuration,
          auto_read_after_ms: autoReadAfterMs,
        };
        await ctx.addNotification(payload);
      }
    },
    [ctx]
  );

  return {
    notify,
    notifications: ctx.notifications,
    unreadCount: ctx.unreadCount,
    isLoading: ctx.isLoading,
    markAsRead: ctx.markAsRead,
    markAllAsRead: ctx.markAllAsRead,
    dismiss: ctx.dismiss,
    dismissAll: ctx.dismissAll,
    refetch: ctx.refetch,
    fetchNotifications: ctx.fetchNotifications,
  };
}
